import { useState } from 'react';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten } from '../../../gemeinsam/lib/daten';
import type { Lieferant } from '../../../gemeinsam/lib/typen';
import { Abzeichen, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner } from '../../../gemeinsam/komponenten/Basis';
import LieferantAkte from '../komponenten/LieferantAkte';
import { euro, datum } from '../../../gemeinsam/lib/daten';
import { useSprache } from '../../../gemeinsam/lib/i18n';

const LEER = {
  name: '', lieferantennummer: '', ansprechpartner: '', email: '', telefon: '',
  strasse: '', plz: '', ort: '', land: 'Deutschland', ustId: '', notiz: '',
};

export default function Lieferanten() {
  const { t } = useSprache();
  const [suche, setSuche] = useState('');
  const { daten, laedt, fehler, neuLaden } = useDaten<Lieferant[]>(
    `/suppliers${suche ? `?suche=${encodeURIComponent(suche)}` : ''}`, [suche]);

  const [formular, setFormular] = useState<typeof LEER & { id?: string } | null>(null);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [akteId, setAkteId] = useState<string | null>(null);

  async function speichern(e: React.FormEvent) {
    e.preventDefault();
    if (!formular) return;
    setSendeFehler(null);
    setSendet(true);
    try {
      const { id, ...felder } = formular;
      if (id) await api(`/suppliers/${id}`, { methode: 'PUT', daten: felder });
      else await api('/suppliers', { methode: 'POST', daten: felder });
      setFormular(null);
      setMeldung(id ? 'Lieferant gespeichert.' : 'Lieferant angelegt.');
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSendet(false);
    }
  }

  async function loeschen(l: Lieferant) {
    if (!confirm(`„${l.name}“ wirklich löschen? Zugeordnete Artikel bleiben erhalten.`)) return;
    try {
      await api(`/suppliers/${l.id}`, { methode: 'DELETE' });
      setMeldung(`${l.name} wurde gelöscht.`);
      await neuLaden();
    } catch (e) {
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.');
    }
  }

  if (akteId && !formular) {
    return (
      <div className="mx-auto max-w-5xl space-y-4">
        <LieferantAkte id={akteId} beiZurueck={() => { setAkteId(null); void neuLaden(); }}
                       beiBearbeiten={l => setFormular({ ...LEER, ...l, id: l.id })} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('lieferanten.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {daten ? `${daten.length} ${daten.length === 1 ? 'Lieferant' : 'Lieferanten'}` : '…'}
          </p>
        </div>
        {!formular && (
          <Knopf variante="primaer" onClick={() => { setFormular({ ...LEER }); setMeldung(null); }}>
            {t('lieferanten.neu')}
          </Knopf>
        )}
      </div>

      {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
      {sendeFehler && <Hinweis art="fehler">{sendeFehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {formular && (
        <Karte>
          <KartenKopf titel={formular.id ? 'Lieferant bearbeiten' : 'Neuer Lieferant'} />
          <form onSubmit={speichern} className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Feld label={t('feld.name')} value={formular.name} required autoFocus
                    onChange={e => setFormular(f => f && ({ ...f, name: e.target.value }))} />
              <Feld label={t('lieferanten.nummer')} value={formular.lieferantennummer}
                    onChange={e => setFormular(f => f && ({ ...f, lieferantennummer: e.target.value }))} />
              <Feld label={t('feld.ansprechpartner')} value={formular.ansprechpartner}
                    onChange={e => setFormular(f => f && ({ ...f, ansprechpartner: e.target.value }))} />
              <Feld label={t('feld.email')} type="email" value={formular.email}
                    onChange={e => setFormular(f => f && ({ ...f, email: e.target.value }))} />
              <Feld label={t('feld.telefon')} value={formular.telefon}
                    onChange={e => setFormular(f => f && ({ ...f, telefon: e.target.value }))} />
              <Feld label={t('feld.strasse')} value={formular.strasse}
                    onChange={e => setFormular(f => f && ({ ...f, strasse: e.target.value }))} />
              <div className="grid grid-cols-[100px_1fr] gap-3">
                <Feld label={t('feld.plz')} value={formular.plz}
                      onChange={e => setFormular(f => f && ({ ...f, plz: e.target.value }))} />
                <Feld label={t('feld.ort')} value={formular.ort}
                      onChange={e => setFormular(f => f && ({ ...f, ort: e.target.value }))} />
              </div>
              <Feld label={t('feld.notiz')} value={formular.notiz}
                    onChange={e => setFormular(f => f && ({ ...f, notiz: e.target.value }))} />
            </div>
            <div className="flex gap-2">
              <Knopf type="submit" variante="primaer" laedt={sendet}>{t('aktion.speichern')}</Knopf>
              <Knopf type="button" onClick={() => { setFormular(null); setSendeFehler(null); }}>{t('aktion.abbrechen')}</Knopf>
            </div>
          </form>
        </Karte>
      )}

      <Karte>
        <div className="border-b border-slate-200 p-4 dark:border-slate-800">
          <Feld label={t('allg.suche')} value={suche} placeholder={t('lieferanten.suche')}
                onChange={e => setSuche(e.target.value)} />
        </div>

        {laedt && !daten ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !daten?.length ? (
          <LeerZustand titel={suche ? t('allg.keineTreffer') : t('lieferanten.leer')} />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {daten.map(l => (
              <li key={l.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                {/* Ein Klick öffnet die Akte: Ware, Lieferungen, Rechnungen. */}
                <button type="button" onClick={() => { setAkteId(l.id); setMeldung(null); }}
                        className="min-w-[200px] flex-1 text-left">
                  <p className="font-medium text-slate-900 hover:text-marke-700 dark:text-slate-100 dark:hover:text-marke-300">{l.name}</p>
                  <p className="truncate text-sm text-slate-500 dark:text-slate-400">
                    {[l.ansprechpartner, l.email, l.telefon].filter(Boolean).join(' · ') || 'Keine Kontaktdaten'}
                  </p>
                  {l.kennzahlen && (
                    <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                      {l.kennzahlen.artikel} {t('lieferanten.artikel')} · {l.kennzahlen.lieferungen} {t('lieferanten.lieferungen')}
                      {l.kennzahlen.letzteLieferung && ` · ${t('lieferanten.letzteLieferung')} ${datum(l.kennzahlen.letzteLieferung)}`}
                    </p>
                  )}
                </button>
                {(l.kennzahlen?.offenAnzahl ?? 0) > 0 && (
                  <Abzeichen art={(l.kennzahlen?.ueberfaelligAnzahl ?? 0) > 0 ? 'rot' : 'gelb'}>
                    {t('lieferanten.offen')} {euro(l.kennzahlen?.offen ?? 0)}
                  </Abzeichen>
                )}
                <div className="flex gap-1.5">
                  <Knopf variante="primaer" className="!text-xs" onClick={() => { setAkteId(l.id); setMeldung(null); }}>
                    {t('lieferanten.akte')}
                  </Knopf>
                  <Knopf variante="still" className="!text-xs"
                         onClick={() => { setFormular({ ...LEER, ...l, id: l.id }); setMeldung(null); }}>
                    {t('lieferanten.bearbeiten')}
                  </Knopf>
                  <Knopf variante="still" className="!text-xs" onClick={() => loeschen(l)}>{t('lieferanten.loeschen')}</Knopf>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}
